import { apiClient } from "@/services/apiClient";

/**
 * The admin's professionals, across every profession in the registry
 * (spec 2f0e5645).
 *
 * WHY THIS REPLACES TWO MODULES. `notariesService` and `accountantsService`
 * were the same file twice, and the cost of that was not duplication — it was
 * that adding a profession meant writing a third copy, in the one place a
 * human has to work when a profession is added. Nothing here names a
 * profession; the slug is a value that travels, never a branch.
 */

/** One row in the registry. The admin UI builds its choices from this. */
export interface ProfessionOption {
  slug: string;
  displayName: string;
  /** What the detail page dispatches on (decision a78bb4f). */
  verificationStrategy: string | null;
  copyNamespace: string | null;
}

export interface ProfessionalUser {
  id: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  profileImage: string | null;
  status: string;
  createdAt: string;
  professionSlug: string;
  professionDisplayName: string | null;
  verificationStrategy: string | null;
  activationStatus: "pending" | "active" | null;
  fiscalStatus: string;
  activeAssignments: number;
  activeAssignmentLimit: number;
}

export interface PendingProfessionalInvite {
  id: string;
  professionSlug: string;
  email: string;
  invitedByUserId: string;
  invitedByEmail: string | null;
  expiresAt: string;
  createdAt: string;
  expired: boolean;
}

export async function listProfessions(): Promise<ProfessionOption[]> {
  const { data } = await apiClient.get<ProfessionOption[]>("/platform/professions");
  return data;
}

/** Every professional, or one profession's, when the admin filters. */
export async function listProfessionals(
  professionSlug?: string,
): Promise<ProfessionalUser[]> {
  const { data } = await apiClient.get<ProfessionalUser[]>("/platform/professionals", {
    params: professionSlug ? { professionSlug } : undefined,
  });
  return data;
}

export async function listPendingProfessionalInvites(
  professionSlug?: string,
): Promise<PendingProfessionalInvite[]> {
  const { data } = await apiClient.get<PendingProfessionalInvite[]>(
    "/platform/professional-invites",
    { params: professionSlug ? { professionSlug } : undefined },
  );
  return data;
}

/**
 * The slug travels to the server, which resolves it against the registry and
 * refuses what it does not know. The client picks from `listProfessions`, so
 * it never invents one — but the server does not trust that, because an
 * invitation binds a person to a profession.
 */
export async function inviteProfessional(
  professionSlug: string,
  email: string,
): Promise<void> {
  await apiClient.post("/auth/professional-invite", {
    professionSlug,
    email: email.trim().toLowerCase(),
  });
}

export async function resendProfessionalInvite(
  inviteId: string,
  professionSlug: string,
): Promise<void> {
  await apiClient.post(
    `/platform/professional-invites/${encodeURIComponent(inviteId)}/resend`,
    { professionSlug },
  );
}

export async function cancelProfessionalInvite(
  inviteId: string,
  professionSlug: string,
): Promise<void> {
  await apiClient.delete(
    `/platform/professional-invites/${encodeURIComponent(inviteId)}`,
    { params: { professionSlug } },
  );
}

/**
 * Remove a professional's membership, whatever they practise.
 *
 * NOT deleteNotary. The named deletes refuse a user whose membership is not
 * their own profession, so routing the unified list through one of them would
 * delete notaries and refuse everyone else — with a "Notary not found" an
 * admin would read as the row being stale.
 */
export async function deleteProfessional(userId: string): Promise<void> {
  await apiClient.delete(`/platform/professionals/${encodeURIComponent(userId)}`);
}
